import { createFeatureSelector, createSelector } from '@ngrx/store';

import { groupArrayByKey } from '@campus/utils';
import { Dictionary } from '@ngrx/entity';
import { NewsCategoryEnum, NewsInterface } from '../../+models';
import { MethodQueries } from '../method';
import { MethodNewsQueries } from '../method-news';
import { NewsCategoryQueries } from '../news-category';
import { NewsCategoryNewsQueries } from '../news-category-news';
import { PersonPreferenceQueries } from '../person-preference';
import { NAME, selectAll, selectEntities, State } from './news.reducer';

export const selectNewsState = createFeatureSelector<State>(NAME);

export const getError = createSelector(selectNewsState, (state: State) => state.error);

export const getLoaded = createSelector(selectNewsState, (state: State) => state.loaded);

export const getAll = createSelector(selectNewsState, selectAll);

export const getAllEntities = createSelector(selectNewsState, selectEntities);

export const getNewsWithMethodsAndCategories = createSelector(
  getAll,
  MethodQueries.getAllEntities,
  MethodNewsQueries.getAll,
  NewsCategoryNewsQueries.getByNewsId,
  NewsCategoryQueries.getAllEntities,
  (allNews, methodEntities, allMethodNews, nCnByNewsId, newsCategoryDict): NewsInterface[] => {
    const methodNewsByNewsId = groupArrayByKey(allMethodNews, 'newsId');

    return allNews.map((news: NewsInterface) => {
      const methods = (methodNewsByNewsId[news.id] || []).map((method) => methodEntities[method.methodId]);
      const categories = (nCnByNewsId[news.id] || []).map((nCN) => newsCategoryDict[nCN.newsCategoryId]);
      return { ...news, methods, categories };
    });
  }
);

export const getNewsWithMethodsByCategoryId = createSelector(
  getNewsWithMethodsAndCategories,
  NewsCategoryNewsQueries.getAll,
  NewsCategoryQueries.getAll,
  PersonPreferenceQueries.getByKey,
  (newsWithMethods, allNewsCategoryNews, newsCategories, personPreferencesByKey): Dictionary<NewsInterface[]> => {
    const newsCategoryNewsByNewsId = groupArrayByKey(allNewsCategoryNews, 'newsId');
    const announcementCategoryId = newsCategories.find((cat) => cat.label === NewsCategoryEnum.AANKONDIGING)?.id;
    const tipsCategoryId = newsCategories.find((cat) => cat.label === NewsCategoryEnum.TIPS)?.id;
    const readNewsIds = (personPreferencesByKey['NEWS']?.[0]?.value as number[]) ?? [];

    return newsWithMethods.reduce(
      (acc: Dictionary<NewsInterface[]>, news: NewsInterface): Dictionary<NewsInterface[]> => {
        const categoryIds = newsCategoryNewsByNewsId[news.id]?.map((category) => category.newsCategoryId) ?? [];

        categoryIds.forEach((categoryId) => {
          const isReadAnnouncement =
            categoryId === announcementCategoryId && tipsCategoryId != null && readNewsIds.includes(news.id);
          const targetCategoryId = isReadAnnouncement ? tipsCategoryId : categoryId;

          acc[targetCategoryId] = [...(acc[targetCategoryId] || []), { ...news }];
        });

        return acc;
      },
      {}
    );
  }
);

export const getById = createSelector(
  selectNewsState,
  (state: State, props: { id: number }) => state.entities[props.id]
);
